'use client';

import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import { Check, ChevronRight } from 'lucide-react';
import type * as React from 'react';
import { Kbd } from '@/components/molecules/data/Kbd';
import { DropdownMenu as DropdownMenuPrimitive } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// ============================================================================
// Base Primitives
// ============================================================================

function DropdownMenuRoot({ ...props }: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root data-slot="dropdown-menu-root" {...props} />;
}
DropdownMenuRoot.displayName = 'DropdownMenu.Root';

function DropdownMenuPortal({ ...props }: MenuPrimitive.Portal.Props) {
  return <MenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />;
}
DropdownMenuPortal.displayName = 'DropdownMenu.Portal';

function DropdownMenuTrigger({ ...props }: MenuPrimitive.Trigger.Props) {
  return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}
DropdownMenuTrigger.displayName = 'DropdownMenu.Trigger';

function DropdownMenuGroup({ ...props }: MenuPrimitive.Group.Props) {
  return <MenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />;
}
DropdownMenuGroup.displayName = 'DropdownMenu.Group';

function DropdownMenuSub({ ...props }: MenuPrimitive.SubmenuRoot.Props) {
  return <MenuPrimitive.SubmenuRoot data-slot="dropdown-menu-sub" {...props} />;
}
DropdownMenuSub.displayName = 'DropdownMenu.Sub';

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props & {
  inset?: boolean;
}) {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-open:bg-accent data-open:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground gap-2 rounded-sm px-2 py-2 text-sm [&_svg:not([class*='size-'])]:size-4 group/dd-sub flex cursor-default items-center outline-hidden select-none data-[inset]:ps-8 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRight className="ms-auto rtl:rotate-180" />
    </MenuPrimitive.SubmenuTrigger>
  );
}
DropdownMenuSubTrigger.displayName = 'DropdownMenu.SubTrigger';

function DropdownMenuRadioGroup({ ...props }: MenuPrimitive.RadioGroup.Props) {
  return (
    <MenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  );
}
DropdownMenuRadioGroup.displayName = 'DropdownMenu.RadioGroup';

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: MenuPrimitive.RadioItem.Props) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground focus:**:text-accent-foreground gap-2 rounded-sm py-2 pe-8 ps-2 text-sm [&_svg:not([class*='size-'])]:size-4 relative flex cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      <span
        className="pointer-events-none absolute end-2 flex items-center justify-center"
        data-slot="dropdown-menu-radio-item-indicator"
      >
        <MenuPrimitive.RadioItemIndicator className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-(--duration-fast)">
          <Check />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  );
}
DropdownMenuRadioItem.displayName = 'DropdownMenu.RadioItem';

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: MenuPrimitive.CheckboxItem.Props) {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground focus:**:text-accent-foreground gap-2 rounded-sm py-2 pe-8 ps-2 text-sm [&_svg:not([class*='size-'])]:size-4 relative flex cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      checked={checked}
      {...props}
    >
      <span
        className="pointer-events-none absolute end-2 flex items-center justify-center"
        data-slot="dropdown-menu-checkbox-item-indicator"
      >
        <MenuPrimitive.CheckboxItemIndicator className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-(--duration-fast)">
          <Check />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}
DropdownMenuCheckboxItem.displayName = 'DropdownMenu.CheckboxItem';

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        'text-muted-foreground group-focus/dropdown-menu-item:text-accent-foreground ms-auto text-xs tracking-widest',
        className,
      )}
      {...props}
    />
  );
}
DropdownMenuShortcut.displayName = 'DropdownMenu.Shortcut';

// ============================================================================
// Enhanced Components
// ============================================================================

// Width variant classes for popup
const popupWidthClasses = {
  auto: '',
  sm: 'w-48',
  md: 'w-56',
  lg: 'w-64',
};

/**
 * Enhanced DropdownMenuPopup with HumainOS styling
 */
export interface DropdownMenuPopupProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Menu content */
  children?: React.ReactNode;
  /** Width of the dropdown menu */
  width?: 'auto' | 'sm' | 'md' | 'lg';
  /** Side offset from the trigger */
  sideOffset?: number;
  /** Side to show the menu on */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Alignment of the menu */
  align?: 'start' | 'center' | 'end';
  /** Align offset */
  alignOffset?: number;
  /**
   * DOM node to portal the popup into. Falls back to
   * `usePortalContainerOverride()` (which checks `PortalContainerProvider`), then to the
   * base-ui default (the document body).
   */
  container?: HTMLElement | null;
}

function DropdownMenuPopup({
  align = 'start',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  width = 'auto',
  className,
  container,
  ...props
}: DropdownMenuPopupProps) {
  return (
    <DropdownMenuPrimitive.Popup
      container={container}
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      className={cn(
        'border border-border rounded-lg shadow-sm ring-0',
        popupWidthClasses[width],
        className,
      )}
      {...props}
    />
  );
}
DropdownMenuPopup.displayName = 'DropdownMenu.Popup';

function DropdownMenuSubPopup({
  align = 'start',
  alignOffset = -3,
  side = 'right',
  sideOffset = 0,
  className,
  ...props
}: DropdownMenuPopupProps) {
  return (
    <DropdownMenuPopup
      data-slot="dropdown-menu-sub-popup"
      className={cn('min-w-[96px] w-auto', className)}
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      {...props}
    />
  );
}
DropdownMenuSubPopup.displayName = 'DropdownMenu.SubPopup';

/**
 * Header section for dropdown menu with avatar and user info
 */
export interface DropdownMenuHeaderProps extends React.ComponentProps<'div'> {
  /** Avatar element to display */
  avatar?: React.ReactNode;
  /** Primary text (e.g., user name) */
  title?: string;
  /** Secondary text (e.g., email) */
  subtitle?: string;
}

function DropdownMenuHeader({
  className,
  avatar,
  title,
  subtitle,
  children,
  ...props
}: DropdownMenuHeaderProps) {
  if (children) {
    return (
      <div
        data-slot="dropdown-menu-header"
        className={cn('border-b border-border px-4 py-3', className)}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      data-slot="dropdown-menu-header"
      className={cn('border-b border-border px-4 py-3', className)}
      {...props}
    >
      <div className="flex items-center gap-3">
        {avatar}
        <div className="flex flex-col min-w-0">
          {title && (
            <span className="text-sm font-semibold text-foreground truncate">
              {title}
            </span>
          )}
          {subtitle && (
            <span className="text-sm text-muted-foreground truncate">
              {subtitle}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
DropdownMenuHeader.displayName = 'DropdownMenu.Header';

export interface DropdownMenuLabelProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether the label is inset */
  inset?: boolean;
}

/**
 * Enhanced DropdownMenuLabel
 */
function DropdownMenuLabel({
  className,
  inset,
  ...props
}: DropdownMenuLabelProps) {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        'px-4 py-2 text-xs font-medium text-secondary-foreground uppercase tracking-wider',
        'data-[inset]:ps-8',
        className,
      )}
      {...props}
    />
  );
}
DropdownMenuLabel.displayName = 'DropdownMenu.Label';

/**
 * Enhanced DropdownMenuItem with icon and shortcut support
 */
export interface DropdownMenuItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  /** Item content */
  children?: React.ReactNode;
  /** Icon displayed before the label */
  icon?: React.ReactNode;
  /** Keyboard shortcut displayed on the right */
  shortcut?: string;
  /** Makes the item destructive (red text) */
  destructive?: boolean;
  /** Whether the item is disabled */
  disabled?: boolean;
  /**
   * Render as a different element (Base UI pattern).
   * Use this to render as a custom component like Link or anchor.
   * @example render={<Link to="/profile" />}
   */
  render?: React.ReactElement;
  /** Whether the item is inset */
  inset?: boolean;
  /** Variant for styling */
  variant?: 'default' | 'destructive';
}

function DropdownMenuItem({
  className,
  icon,
  shortcut,
  destructive = false,
  render,
  inset,
  variant = 'default',
  children,
  ...props
}: DropdownMenuItemProps) {
  const isDestructive = destructive || variant === 'destructive';

  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={isDestructive ? 'destructive' : variant}
      className={cn(
        'group/dropdown-menu-item relative flex items-center gap-2 mx-1.5 px-2.5 py-2 rounded-md cursor-pointer outline-none',
        'text-sm font-semibold text-secondary-foreground',
        'focus:bg-muted focus:text-secondary-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        'data-[inset]:ps-8',
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        isDestructive &&
          'text-destructive focus:text-destructive focus:bg-destructive/10',
        className,
      )}
      render={render}
      {...props}
    >
      {icon && (
        <span
          className={cn(
            'size-4 shrink-0',
            isDestructive ? 'text-destructive' : 'text-current',
          )}
        >
          {icon}
        </span>
      )}
      <span className="flex-1">{children}</span>
      {shortcut && (
        <DropdownMenuShortcutBadge className="group-focus/dropdown-menu-item:bg-primary/10 group-focus/dropdown-menu-item:text-primary">
          {shortcut}
        </DropdownMenuShortcutBadge>
      )}
    </MenuPrimitive.Item>
  );
}
DropdownMenuItem.displayName = 'DropdownMenu.Item';

export interface DropdownMenuSeparatorProps
  extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Enhanced DropdownMenuSeparator
 */
function DropdownMenuSeparator({
  className,
  ...props
}: DropdownMenuSeparatorProps) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('my-1 h-px bg-surface-2', className)}
      {...props}
    />
  );
}
DropdownMenuSeparator.displayName = 'DropdownMenu.Separator';

export interface DropdownMenuItemsProps
  extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Container for menu items with proper padding
 */
function DropdownMenuItems({
  className,
  children,
  ...props
}: DropdownMenuItemsProps) {
  return (
    <div
      data-slot="dropdown-menu-items"
      className={cn('py-1', className)}
      {...props}
    >
      {children}
    </div>
  );
}
DropdownMenuItems.displayName = 'DropdownMenu.Items';

/**
 * Styled shortcut badge that matches Figma design (uses Kbd wrapper)
 */
function DropdownMenuShortcutBadge({
  className,
  children,
  ...props
}: React.ComponentProps<'kbd'>) {
  return (
    <Kbd variant="outline" size="xs" className={className} {...props}>
      {children}
    </Kbd>
  );
}
DropdownMenuShortcutBadge.displayName = 'DropdownMenu.ShortcutBadge';

// ============================================================================
// SimpleDropdownMenu (with trigger prop pattern)
// ============================================================================

export interface SimpleDropdownMenuProps {
  /** The element that triggers the dropdown (required) */
  trigger: React.ReactElement;
  /** Dropdown content (typically DropdownMenu.Popup component) */
  children?: React.ReactNode;
  /** Controlled open state. When provided, component is controlled. */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Simplified DropdownMenu component with explicit trigger prop.
 * Prevents nested button hydration errors by using base-ui's render prop.
 *
 * @example
 * <SimpleDropdownMenu trigger={<Button>Menu</Button>}>
 *   <DropdownMenu.Popup>
 *     <DropdownMenu.Items>
 *       <DropdownMenu.Item>Option 1</DropdownMenu.Item>
 *     </DropdownMenu.Items>
 *   </DropdownMenu.Popup>
 * </SimpleDropdownMenu>
 */
function SimpleDropdownMenu({
  trigger,
  open,
  onOpenChange,
  children,
}: SimpleDropdownMenuProps) {
  return (
    <DropdownMenuRoot open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger render={trigger} />
      {children}
    </DropdownMenuRoot>
  );
}
SimpleDropdownMenu.displayName = 'DropdownMenu.Simple';

// ============================================================================
// Compound Export (Dot Notation Pattern)
// ============================================================================

export const DropdownMenu = Object.assign(DropdownMenuRoot, {
  Root: DropdownMenuRoot,
  Portal: DropdownMenuPortal,
  Trigger: DropdownMenuTrigger,
  Popup: DropdownMenuPopup,
  Group: DropdownMenuGroup,
  Label: DropdownMenuLabel,
  Item: DropdownMenuItem,
  CheckboxItem: DropdownMenuCheckboxItem,
  RadioGroup: DropdownMenuRadioGroup,
  RadioItem: DropdownMenuRadioItem,
  Separator: DropdownMenuSeparator,
  Shortcut: DropdownMenuShortcut,
  ShortcutBadge: DropdownMenuShortcutBadge,
  Sub: DropdownMenuSub,
  SubTrigger: DropdownMenuSubTrigger,
  SubPopup: DropdownMenuSubPopup,
  Header: DropdownMenuHeader,
  Items: DropdownMenuItems,
  Simple: SimpleDropdownMenu,
});

// ============================================================================
// Type Exports
// ============================================================================

export type DropdownMenuPopupWrapperProps = DropdownMenuPopupProps;
export type DropdownMenuItemWrapperProps = DropdownMenuItemProps;
export type DropdownMenuHeaderWrapperProps = DropdownMenuHeaderProps;
